// The whole sign-in sequence: password → change one-time password → set up / verify MFA.
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { post } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';

function AuthCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="auth-page">
      <header className="auth-band">North Greenville Fitness</header>
      <div className="auth-wrap">
        <div className="card auth-card">
          <div className="brand">
            <img src="/ngf-logo.png" alt="North Greenville Fitness" />
            <div className="muted small">Assessments</div>
          </div>
          <h1 style={{ textAlign: 'center' }}>{title}</h1>
          {children}
        </div>
      </div>
    </div>
  );
}

function useSubmit() {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const run = (fn: () => Promise<void>) => async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return { error, busy, run };
}

export function Login() {
  const { refresh } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { error, busy, run } = useSubmit();
  return (
    <AuthCard title="Sign in">
      {error && <div className="alert error">{error}</div>}
      <form onSubmit={run(async () => { await post('/api/auth/login', { email, password }); await refresh(); })}>
        <div className="field"><label htmlFor="email">Email</label>
          <input id="email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus /></div>
        <div className="field"><label htmlFor="pw">Password</label>
          <input id="pw" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required /></div>
        <button disabled={busy} style={{ width: '100%' }}>{busy ? 'Signing in…' : 'Continue'}</button>
      </form>
      <p className="muted small" style={{ marginTop: '1rem' }}>
        Forgot your password? Ask an NGF administrator to reset your account.
      </p>
    </AuthCard>
  );
}

export function ChangePassword() {
  const { refresh, logout, me } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const { error, busy, run } = useSubmit();
  return (
    <AuthCard title="Choose your password">
      <p className="muted small">Signed in as {me?.email}. Replace the one-time password you were given.</p>
      {error && <div className="alert error">{error}</div>}
      <form onSubmit={run(async () => {
        if (next !== confirm) throw new Error('The new passwords do not match.');
        await post('/api/auth/password', { current, next });
        await refresh();
      })}>
        <div className="field"><label htmlFor="cur">One-time (current) password</label>
          <input id="cur" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required autoFocus /></div>
        <div className="field"><label htmlFor="new">New password</label>
          <input id="new" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} required minLength={12} />
          <div className="hint">At least 12 characters. A short sentence works well.</div></div>
        <div className="field"><label htmlFor="conf">Confirm new password</label>
          <input id="conf" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required /></div>
        <div className="actions">
          <button disabled={busy}>Save password</button>
          <button type="button" className="secondary" onClick={() => void logout()}>Sign out</button>
        </div>
      </form>
    </AuthCard>
  );
}

export function EnrollMfa() {
  const { refresh, logout } = useAuth();
  const [setup, setSetup] = useState<{ secret: string; qrSvg: string } | null>(null);
  const [code, setCode] = useState('');
  const { error, busy, run } = useSubmit();
  useEffect(() => {
    post<{ secret: string; qrSvg: string }>('/api/auth/mfa/enroll').then(setSetup).catch((e) => console.error(e));
  }, []);
  return (
    <AuthCard title="Set up two-step sign-in">
      <ol className="small" style={{ paddingLeft: '1.2rem' }}>
        <li>Open an authenticator app on your phone (Microsoft Authenticator, Google Authenticator, 1Password, Authy).</li>
        <li>Scan this code, or type the key below.</li>
        <li>Enter the 6-digit code the app shows.</li>
      </ol>
      {setup ? (
        <>
          {/* Server-generated SVG from our own API (QR of the otpauth URL). */}
          <div className="qr" dangerouslySetInnerHTML={{ __html: setup.qrSvg }} />
          <p className="small" style={{ textAlign: 'center' }}>Key: <span className="mono">{setup.secret.replace(/(.{4})/g, '$1 ').trim()}</span></p>
        </>
      ) : <p className="muted">Preparing…</p>}
      {error && <div className="alert error">{error}</div>}
      <form onSubmit={run(async () => { await post('/api/auth/mfa/confirm', { code }); await refresh(); })}>
        <div className="field"><label htmlFor="code">6-digit code</label>
          <input id="code" inputMode="numeric" autoComplete="one-time-code" pattern="\d{6}" maxLength={6} value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} required autoFocus /></div>
        <div className="actions">
          <button disabled={busy || !setup}>Confirm</button>
          <button type="button" className="secondary" onClick={() => void logout()}>Sign out</button>
        </div>
      </form>
    </AuthCard>
  );
}

export function VerifyMfa() {
  const { refresh, logout, me } = useAuth();
  const [code, setCode] = useState('');
  const { error, busy, run } = useSubmit();
  return (
    <AuthCard title="Enter your code">
      <p className="muted small">Open your authenticator app and enter the current code for {me?.email}.</p>
      {error && <div className="alert error">{error}</div>}
      <form onSubmit={run(async () => { await post('/api/auth/mfa/verify', { code }); await refresh(); })}>
        <div className="field"><label htmlFor="code">6-digit code</label>
          <input id="code" inputMode="numeric" autoComplete="one-time-code" pattern="\d{6}" maxLength={6} value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} required autoFocus /></div>
        <div className="actions">
          <button disabled={busy}>Verify</button>
          <button type="button" className="secondary" onClick={() => void logout()}>Use a different account</button>
        </div>
      </form>
    </AuthCard>
  );
}
